import React, { useMemo, useState } from "react";
import { Badge, Popover } from "antd";
import "./tab.header.scss";
import {
  CallAddIcon,
  ConversationListIcon,
  ExternalLinkIcon,
  MinimizeIcon,
  MinusIcon,
  SmsIcon,
  XIcon,
} from "@Assets/icons";
import { hasVoiceLicense, IPreviewTab, ISharedFullConversation, TabSizeEnum } from "atlas-shared";
import { IAuth } from "@Store";
import { useTranslation } from "react-i18next";
import { VoiceSuggestions, Icon, ChannelIcon, Counter } from "@Components";
import { preventDefault } from "@Utils";
import { FeatureFlag } from "@Containers/FeatureFlag";
import { useNavigate } from "react-router-dom";

interface IProps {
  tab: IPreviewTab;
  auth: IAuth;
  conversation?: ISharedFullConversation;
  expanded?: boolean;
  infoVisible?: boolean;
  onClose: () => Promise<IPreviewTab>;
  onMaximize?: () => Promise<IPreviewTab>;
  onMinimize?: () => Promise<IPreviewTab>;
  onMini?: () => Promise<IPreviewTab>;
  onMore?: () => void;
  onClick?: () => Promise<IPreviewTab>;
}
export const TabHeader = React.memo(
  ({
    conversation,
    onClose,
    onMaximize,
    onMini,
    onMinimize,
    onMore,
    onClick,
    expanded,
    auth,
    infoVisible,
    tab,
  }: IProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [callStartTime] = useState<number>(0);
    const voiceLicense = useMemo(() => hasVoiceLicense(auth.user), [auth.user]);
    const unread = useMemo(
      () => (conversation ? conversation.unread - conversation.read : 0),
      [conversation?.read, conversation?.unread]
    );

    return (
      <div className={"tab-header " + (expanded && "expanded")}>
        <div className={"title-container"} onClick={onClick}>
          {conversation?.main_channel && (
            <Badge
              size="small"
              style={{ marginTop: 6, marginRight: 3, fontSize: 10 }}
              count={unread}
            >
              <ChannelIcon
                channel={{ c: conversation.main_channel, d: conversation.direction }}
                size={10}
                padding={4}
              />
            </Badge>
          )}
          <span className={"text"}>
            <span>#{conversation?.id}</span>
            <span style={{ opacity: 0.75, paddingLeft: 5, flexShrink: 1 }}>
              {conversation?.title}
            </span>
          </span>
        </div>
        {callStartTime ? <Counter since={callStartTime} /> : undefined}
        <div className={"tab-header-icons"}>
          {auth.user.main && voiceLicense && tab.size === TabSizeEnum.Mini && (
            <Popover
              trigger="click"
              placement="topRight"
              overlayClassName="call-overlay-new-bridge-options"
              content={<VoiceSuggestions conversation={conversation} />}
            >
              <Icon
                icon={CallAddIcon}
                className="add-call"
                iconProps={{ size: 12 }}
                tooltip={{ title: t("ADD_CALL_TO_CONVERSATION") }}
                onClick={preventDefault()}
              />
            </Popover>
          )}
          <FeatureFlag identifier="text.message" defaultValue={false}>
            {onMore && tab.size === TabSizeEnum.Mini && (
              <Icon
                icon={SmsIcon}
                iconProps={{ size: 14 }}
                tooltip={{ title: t("NEW_TEXT_MESSAGE") }}
                onClick={() => {
                  navigate(
                    // The query param "message_type" is used to trigger the conversationMessageReply
                    // in conversation.title.box.icons.tsx.
                    // This opens the text message input in the conversation.
                    `/dashboard/conversation/${auth.user.__organizations[0].organization_id}/${conversation?.id}?message_type=text_message`
                  );
                }}
              />
            )}
          </FeatureFlag>
          {onMore && tab.size === TabSizeEnum.Mini && (
            <Icon
              icon={ConversationListIcon}
              iconProps={{ size: 14 }}
              onClick={preventDefault(onMore)}
              tooltip={{ title: t("CONVERSATION_SUMMARY") }}
              className={infoVisible ? "active" : ""}
            />
          )}
          {onMaximize && (
            <Icon
              icon={ExternalLinkIcon}
              iconProps={{ size: 14 }}
              tooltip={{ title: t("MAXIMIZE") }}
              onClick={preventDefault(onMaximize)}
            />
          )}
          {onMinimize && tab.size === TabSizeEnum.Mini && (
            <Icon
              icon={MinusIcon}
              iconProps={{ size: 14 }}
              tooltip={{ title: t("MINIMIZE") }}
              onClick={preventDefault(onMinimize)}
            />
          )}
          {onMini && (
            <Icon
              icon={MinimizeIcon}
              iconProps={{ size: 14 }}
              tooltip={{ title: t("MINIMIZE") }}
              onClick={preventDefault(onMini)}
            />
          )}
          <Icon
            icon={XIcon}
            iconProps={{ size: 14 }}
            tooltip={{ title: t("CLOSE") }}
            onClick={preventDefault(onClose)}
          />
        </div>
      </div>
    );
  }
);
